import { useState, useMemo, useEffect } from 'react'
import { SideModal } from './SideModal'
import { inputCls } from './finance-shared'
import { FiUploadCloud, FiDownload, FiLoader, FiCheckCircle, FiAlertTriangle, FiArrowLeft, FiArrowRight } from 'react-icons/fi'

// Importar extrato bancário em CSV/planilha (PRD §5.2) — pra bancos que não exportam OFX.
// Reusa /api/import/parse (preview em grade) e envia as linhas mapeadas pra
// /api/reconciliation/import-csv, que insere em imported_bank_movement (mesmo dedup do OFX).

interface ParseResp { success: boolean; grid: string[][]; suggestedHeader: number; error?: string }
interface Acc { id: number; name: string; account_number: string | null }
type Row = { date?: string; amount?: string; memo?: string; __line?: number }

const FIELDS: { key: 'date' | 'amount' | 'memo'; label: string; hints: string[] }[] = [
  { key: 'date',   label: 'Data',      hints: ['data', 'dt', 'lançamento', 'lancamento', 'movimento'] },
  { key: 'amount', label: 'Valor',     hints: ['valor', 'montante', 'r$', 'credito', 'debito'] },
  { key: 'memo',   label: 'Descrição', hints: ['histor', 'descri', 'memo', 'observ', 'lançamento', 'detalhe'] },
]
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim()

// Valor em pt-BR, só para conferência na tela (quem vale é o parseAmount do
// backend). Aceita 1.234,56 e 1234.56.
const valorBr = (s: any) => {
  const t = String(s ?? '').trim().replace(/[R$\s]/g, '')
  if (!t) return 0
  const n = Number(t.replace(/\./g, '').replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

// Extrato SEM sinal: quem manda na direção é o histórico.
//
// Caso que obrigou isto (2026-08-24): o "Extrato de Aplicação — SICREDINVEST"
// da conta de investimento traz TODOS os valores positivos, e a direção só
// existe na coluna Histórico — Aplicações e Rendimentos entram, Resgates e
// Encargos (IRRF/IOF) saem. Importado como estava, o arquivo virava 149
// créditos e +R$ 2,8 milhões numa conta cujo extrato declara saldo 0,00.
//
// ⚠️ A régua é a da CONTA DO ARQUIVO. No extrato da aplicação, "Aplicações" é
// dinheiro CHEGANDO; no extrato da conta corrente, a mesma palavra é dinheiro
// SAINDO. Por isso o palpite é só palpite: quem confirma é quem importa, e o
// líquido aparece na tela para bater com o extrato antes de gravar.
const PALPITE_SAIDA = ['resgate', 'encargo', 'irrf', 'iof', 'tarifa', 'imposto', 'taxa',
  'saque', 'debito', 'pagamento', 'cobranca', 'juros pagos', 'estorno']
const PALPITE_ENTRADA = ['aplicac', 'rendimento', 'credito', 'deposito', 'recebiment', 'transferencia recebida']
const palpiteSinal = (memo: string): 'entrada' | 'saida' => {
  const n = norm(memo)
  if (PALPITE_SAIDA.some(k => n.includes(k))) return 'saida'
  if (PALPITE_ENTRADA.some(k => n.includes(k))) return 'entrada'
  return 'entrada'
}

function downloadModelo() {
  const csv = '﻿' + [['Data', 'Valor', 'Descrição'], ['25/06/2026', '-1.234,56', 'PAGAMENTO FORNECEDOR X'], ['26/06/2026', '510,00', 'RECEBIMENTO PIX']]
    .map(r => r.map(c => `"${c}"`).join(';')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a'); a.href = url; a.download = 'modelo-extrato.csv'; a.click()
  URL.revokeObjectURL(url)
}

export function ImportarExtratoCsv({ authenticatedFetch, onClose, onImported, skipAutoCreate = false }: {
  authenticatedFetch: (url: string, opts?: RequestInit) => Promise<Response>; onClose: () => void; onImported: () => void
  // true = NÃO cria títulos automaticamente via regra (usado pelo De ↔ Para:
  // extrato retroativo já coberto pelo histórico do ERP criaria duplicatas —
  // caso real de 2026-07-08: 169 títulos duplicados num extrato de janeiro).
  skipAutoCreate?: boolean
}) {
  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accounts, setAccounts] = useState<Acc[]>([])
  const [accountId, setAccountId] = useState<number | ''>('')
  const [fileName, setFileName] = useState('')
  const [grid, setGrid] = useState<string[][]>([])
  const [headerRow, setHeaderRow] = useState(0)
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [sinais, setSinais] = useState<Record<string, 'entrada' | 'saida'>>({})
  const [result, setResult] = useState<{ novos: number; dup: number; invalidas: number; autoConciliados: number; autoCriados: number } | null>(null)

  useEffect(() => {
    authenticatedFetch('/api/bank-accounts').then(r => r.json()).then(d => {
      if (d.success) { const rows: (Acc & { is_pj_main?: number })[] = d.items || []; setAccounts(rows); const pj = rows.find(a => a.is_pj_main); if (pj) setAccountId(pj.id) }
    })
  }, [authenticatedFetch])

  const headerCells = grid[headerRow] || []
  const dataRows = useMemo(() => grid.slice(headerRow + 1).filter(r => r.some(c => String(c).trim() !== '')), [grid, headerRow])
  const maxCols = useMemo(() => grid.reduce((m, r) => Math.max(m, r.length), 0), [grid])

  const autoMap = (hdr: string[]) => {
    const m: Record<string, number> = {}
    FIELDS.forEach(f => { m[f.key] = hdr.findIndex(c => { const n = norm(c); return f.hints.some(h => n.includes(h)) }) })
    setMapping(m)
  }

  async function handleFile(file: File) {
    setBusy(true); setError(null)
    try {
      const fd = new FormData(); fd.append('file', file)
      const r = await authenticatedFetch('/api/import/parse', { method: 'POST', body: fd })
      const d: ParseResp = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao ler o arquivo'); return }
      setFileName(file.name); setGrid(d.grid); setHeaderRow(d.suggestedHeader)
      autoMap(d.grid[d.suggestedHeader] || [])
      setStep(2)
    } catch { setError('Erro ao enviar o arquivo') }
    finally { setBusy(false) }
  }

  const missing = FIELDS.filter(f => f.key !== 'memo' && (mapping[f.key] == null || mapping[f.key] < 0))

  // O arquivo traz sinal? Basta UMA linha negativa para o extrato ser
  // auto-suficiente e o bloco de direção não aparecer.
  const semSinal = useMemo(() => {
    const ci = mapping.amount
    if (ci == null || ci < 0 || !dataRows.length) return false
    const temNeg = dataRows.some(r => {
      const v = String(r[ci] ?? '')
      return v.includes('-') || /\(\s*[\d.,]+\s*\)/.test(v)
    })
    return !temNeg && (mapping.memo ?? -1) >= 0
  }, [dataRows, mapping])

  // Um item por histórico distinto, com quantas linhas e quanto soma. Linha de
  // valor zero fica de fora: o backend já a recusa ("valor inválido ou zero") e
  // no extrato do Sicredi são justamente "Saldo Anterior" e "Saldo Atual", que
  // não são movimento.
  const historicos = useMemo(() => {
    if (!semSinal) return [] as { memo: string; n: number; soma: number }[]
    const mi = mapping.memo, ai = mapping.amount
    const m = new Map<string, { memo: string; n: number; soma: number }>()
    for (const r of dataRows) {
      const v = valorBr(r[ai])
      if (!v) continue
      const k = String(r[mi] ?? '').trim()
      if (!k) continue
      if (!m.has(k)) m.set(k, { memo: k, n: 0, soma: 0 })
      const a = m.get(k)!; a.n++; a.soma += v
    }
    return [...m.values()].sort((a, b) => b.soma - a.soma)
  }, [dataRows, mapping, semSinal])

  // Palpite inicial a cada troca de mapeamento; a escolha de quem importa
  // sobrevive porque só preenche histórico ainda não decidido.
  useEffect(() => {
    if (!historicos.length) return
    setSinais(prev => {
      const out = { ...prev }
      let mudou = false
      for (const h of historicos) if (!out[h.memo]) { out[h.memo] = palpiteSinal(h.memo); mudou = true }
      return mudou ? out : prev
    })
  }, [historicos])

  const sinalDe = (memo: string) => sinais[String(memo || '').trim()] || palpiteSinal(String(memo || ''))

  const conferencia = useMemo(() => {
    if (!semSinal) return null
    let entradas = 0, saidas = 0
    for (const h of historicos) (sinalDe(h.memo) === 'saida' ? (saidas += h.soma) : (entradas += h.soma))
    return { entradas, saidas, liquido: entradas - saidas }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicos, sinais, semSinal])

  const buildRows = (): Row[] => dataRows.map(row => {
    const o: Row = {}
    FIELDS.forEach(f => { const ci = mapping[f.key]; if (ci != null && ci >= 0) o[f.key] = row[ci] ?? '' })
    // O sinal entra como texto no próprio valor: o backend já lê "-1.234,56" e
    // é ele quem decide crédito/débito (valor > 0). Nada de campo novo na API.
    if (semSinal && o.amount != null && sinalDe(o.memo || '') === 'saida') {
      const v = String(o.amount).trim()
      if (v && valorBr(v) !== 0) o.amount = '-' + v
    }
    const idx = grid.indexOf(row); o.__line = idx >= 0 ? idx + 1 : undefined
    return o
  })

  // Detecção inteligente da conta: ao entrar na etapa Confirmar, cruza os
  // valores do arquivo com os lançamentos pagos de CADA conta (valor exato,
  // data ±3d) e sugere a dona provável — pega extrato importado na conta
  // errada (caso real: extrato da Caixa indo pra Sicredi) e também mapeamento
  // errado (coluna Saldo como Valor → quase nada casa em conta nenhuma).
  interface DetectScore { account_id: number; name: string; matches: number }
  const [detect, setDetect] = useState<{ total: number; scores: DetectScore[]; best: DetectScore | null } | null>(null)
  useEffect(() => {
    if (step !== 3) { setDetect(null); return }
    let cancelled = false
    ;(async () => {
      try {
        const rows = buildRows().slice(0, 600).map(r => ({ date: r.date, amount: r.amount }))
        const r = await authenticatedFetch('/api/reconciliation/detect-account', { method: 'POST', body: JSON.stringify({ rows }) })
        const d = await r.json()
        if (!cancelled && d.success) setDetect(d)
      } catch { /* sugestão é opcional — não bloqueia o import */ }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  async function commit() {
    setBusy(true); setError(null)
    try {
      const r = await authenticatedFetch('/api/reconciliation/import-csv', {
        method: 'POST', body: JSON.stringify({ bank_account_id: accountId, filename: fileName, rows: buildRows(), skip_auto_create: skipAutoCreate }),
      })
      const d = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao importar'); return }
      setResult(d.extrato); setStep(4)
      if (d.extrato.novos > 0) onImported()
    } catch { setError('Erro ao importar extrato') }
    finally { setBusy(false) }
  }

  const steps = ['Carregar', 'Mapear', 'Confirmar', 'Concluir']
  const footer = (
    <>
      {step > 1 && step < 4 && <button onClick={() => { setError(null); setStep(s => s - 1) }} disabled={busy} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border h-9 px-3.5 text-sm hover:bg-secondary disabled:opacity-40"><FiArrowLeft className="w-4 h-4" /> Voltar</button>}
      {step === 2 && <button onClick={() => { setError(null); setStep(3) }} disabled={busy || missing.length > 0 || !accountId} className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground h-9 px-4 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">Continuar <FiArrowRight className="w-4 h-4" /></button>}
      {step === 3 && <button onClick={commit} disabled={busy || dataRows.length === 0} className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground h-9 px-4 text-sm font-medium hover:opacity-90 disabled:opacity-40">{busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />} Importar {dataRows.length} movimento{dataRows.length === 1 ? '' : 's'}</button>}
      {step === 4 && <button onClick={onClose} className="neu-relief h-9 rounded-lg bg-primary text-primary-foreground px-4 text-sm font-medium hover:opacity-90">Fechar</button>}
    </>
  )

  return (
    <SideModal title="Importar extrato em CSV/planilha" subtitle={fileName || 'XLS, XLSX ou CSV até 10 MB'} icon={FiUploadCloud} onClose={onClose} maxWidth="max-w-2xl" footer={footer}>
      {/* PLANOS (21/10/2026): passos numa faixa de papel; cada passo mora numa
          caixa com título (15px) e respiro p-4; textos de apoio ≥12.5px. */}
      <div className="neu-card flex items-center flex-wrap gap-2 text-[13px] rounded-xl border border-border bg-card px-4 py-3">
        {steps.map((s, i) => {
          const n = i + 1, done = n < step, cur = n === step
          return (<div key={s} className="flex items-center gap-2">
            <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${done ? 'bg-primary text-primary-foreground' : cur ? 'bg-primary/15 text-primary ring-1 ring-primary' : 'bg-secondary text-muted-foreground'}`}>{n}</span>
            <span className={cur ? 'text-foreground font-medium' : 'text-muted-foreground'}>{s}</span>
            {i < steps.length - 1 && <span className="w-5 h-px bg-border mx-0.5" />}
          </div>)
        })}
      </div>
      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400 flex items-center gap-2"><FiAlertTriangle className="w-4 h-4 shrink-0" />{error}</div>}

      {step === 1 && (
        <section className="neu-card rounded-xl border border-border bg-card p-4 space-y-4">
          <h4 className="text-[15px] font-semibold text-foreground">Conta e arquivo</h4>
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Conta do extrato</span>
            <select value={accountId} onChange={e => setAccountId(e.target.value ? Number(e.target.value) : '')} className={inputCls + ' mt-1'}>
              <option value="">Selecione a conta…</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <button onClick={downloadModelo} className="inline-flex items-center gap-2 text-sm text-primary hover:underline"><FiDownload className="w-4 h-4" /> Baixar modelo de CSV</button>
          <label className={`block border-2 border-dashed border-border rounded-xl p-8 text-center transition-colors ${accountId ? 'cursor-pointer hover:border-primary/50 hover:bg-secondary/30' : 'opacity-50 cursor-not-allowed'}`}>
            <input type="file" accept=".xls,.xlsx,.csv" className="hidden" disabled={busy || !accountId} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
            {busy ? <FiLoader className="w-8 h-8 mx-auto animate-spin text-muted-foreground" /> : <FiUploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />}
            <div className="mt-2 text-sm text-foreground font-medium">{accountId ? 'Clique para selecionar o arquivo' : 'Selecione a conta primeiro'}</div>
            <div className="text-[12.5px] text-muted-foreground mt-1">Valor negativo = débito · positivo = crédito.</div>
          </label>
        </section>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <section className="neu-card rounded-xl border border-border bg-card p-4 space-y-4">
          <h4 className="text-[15px] font-semibold text-foreground">Colunas do arquivo</h4>
          <div className="flex items-center flex-wrap gap-2 text-sm">
            <span className="text-muted-foreground">Linha de cabeçalho:</span>
            <select value={headerRow} onChange={e => { const h = Number(e.target.value); setHeaderRow(h); autoMap(grid[h] || []) }} className="neu-convex h-9 rounded-lg border border-border bg-card px-2 text-sm">
              {grid.slice(0, 15).map((r, i) => <option key={i} value={i}>Linha {i + 1} — {r.filter(c => String(c).trim()).slice(0, 3).join(', ').slice(0, 40) || '(vazia)'}</option>)}
            </select>
          </div>
          <p className="text-[12.5px] text-muted-foreground">Relacione Data e Valor (obrigatórios) e Descrição às colunas do arquivo.</p>
          <div className="space-y-2.5">
            {FIELDS.map(f => (
              <div key={f.key} className="flex items-center gap-3">
                <label className="text-sm w-28 shrink-0 text-foreground">{f.label}{f.key !== 'memo' && <span className="text-red-500"> *</span>}</label>
                <select value={mapping[f.key] ?? -1} onChange={e => setMapping(m => ({ ...m, [f.key]: Number(e.target.value) }))}
                  className={`flex-1 h-9 rounded-lg border bg-card px-2 text-sm ${f.key !== 'memo' && (mapping[f.key] ?? -1) < 0 ? 'border-red-400' : 'border-border'}`}>
                  <option value={-1}>— ignorar —</option>
                  {Array.from({ length: maxCols }, (_, ci) => <option key={ci} value={ci}>{headerCells[ci]?.trim() || `Coluna ${ci + 1}`}</option>)}
                </select>
              </div>
            ))}
          </div>
          {missing.length > 0 && <div className="text-[13px] text-amber-600 dark:text-amber-400">Falta mapear: {missing.map(f => f.label).join(', ')}.</div>}
          </section>

          {/* Direção por histórico — só aparece quando o arquivo não traz sinal. */}
          {semSinal && historicos.length > 0 && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-3">
              <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                <FiAlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Neste arquivo <strong>nenhum valor é negativo</strong>: quem diz se o dinheiro entrou ou saiu é o histórico.
                  Confira cada tipo abaixo — o palpite é automático, mas a régua muda conforme a conta do extrato
                  (numa aplicação, “Aplicações” é entrada; na conta corrente, é saída).
                </span>
              </div>
              <div className="space-y-1">
                {historicos.map(h => {
                  const sin = sinalDe(h.memo)
                  return (
                    <div key={h.memo} className="flex items-center gap-2 text-sm">
                      <span className="flex-1 min-w-0 truncate" title={h.memo}>{h.memo}</span>
                      <span className="text-[12.5px] text-muted-foreground tabular-nums whitespace-nowrap">
                        {h.n}× · {h.soma.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                      <div className="neu-seg inline-flex items-center gap-1 shrink-0">
                        {(['entrada', 'saida'] as const).map(op => (
                          <button key={op} onClick={() => setSinais(m => ({ ...m, [h.memo]: op }))}
                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${sin === op
                              ? (op === 'entrada' ? 'neu-relief bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                                                  : 'neu-relief bg-red-500/15 text-red-700 dark:text-red-400')
                              : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
                            {op === 'entrada' ? 'entra' : 'sai'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
              {conferencia && (
                // O número que fecha a conferência: compare com a variação de
                // saldo declarada no próprio extrato antes de importar.
                <div className="border-t border-amber-500/30 pt-2.5 text-[13px] flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
                  <span className="text-emerald-700 dark:text-emerald-400">
                    entradas {conferencia.entradas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                  <span className="text-red-700 dark:text-red-400">
                    saídas {conferencia.saidas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                  <span className="font-medium">
                    variação no período {conferencia.liquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">

          {/* Detecção inteligente da conta (cruzamento valor+data com o sistema) */}
          {detect && (() => {
            const chosen = detect.scores.find(s => s.account_id === accountId)
            if (detect.best && detect.best.account_id !== accountId) {
              return (
                <div className="neu-relief flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
                  <FiAlertTriangle className="w-4 h-4 shrink-0" />
                  <span>
                    Este extrato parece ser da conta <strong>{detect.best.name}</strong>: {detect.best.matches} de {detect.total} valores casam
                    com lançamentos dela{chosen ? ` (na conta escolhida casam só ${chosen.matches})` : ''}.
                  </span>
                  <button onClick={() => setAccountId(detect.best!.account_id)}
                    className="neu-relief h-8 rounded-lg border border-amber-500/50 px-3 text-[13px] font-medium hover:bg-amber-500/20">
                    Usar {detect.best.name}
                  </button>
                </div>
              )
            }
            if (detect.best) {
              return (
                <div className="neu-relief flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
                  <FiCheckCircle className="w-4 h-4 shrink-0" />
                  <span>Confere: {detect.best.matches} de {detect.total} valores casam com lançamentos da conta escolhida.</span>
                </div>
              )
            }
            return (
              <div className="neu-relief flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
                <FiAlertTriangle className="w-4 h-4 shrink-0" />
                <span>Quase nenhum valor do arquivo casa com lançamentos do sistema — confira o mapeamento: a coluna Valor deve ser o valor do lançamento (não a coluna Saldo).</span>
              </div>
            )
          })()}
          <section className="neu-card rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap px-4 py-3 border-b border-border/60">
            <h4 className="text-[15px] font-semibold text-foreground">Prévia</h4>
            <span className="text-[13px] text-muted-foreground">
              {dataRows.length} linha{dataRows.length === 1 ? '' : 's'} pronta{dataRows.length === 1 ? '' : 's'}. Confira antes de importar.
              {dataRows.length > 100 && <> Mostrando as 100 primeiras de {dataRows.length}.</>}
            </span>
          </div>
          <div className="overflow-x-auto max-h-[46vh]">
            <table className="w-full text-[13px] min-w-[420px]">
              <thead className="sticky top-0 bg-card"><tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="pl-4 pr-2 py-2 font-medium">Data</th><th className="px-2 py-2 font-medium">Descrição</th><th className="pl-2 pr-4 py-2 font-medium text-right">Valor</th>
              </tr></thead>
              <tbody>
                {buildRows().slice(0, 100).map((r, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="pl-4 pr-2 py-2 whitespace-nowrap">{r.date || '—'}</td>
                    <td className="px-2 py-2 truncate max-w-[220px]">{r.memo || '—'}</td>
                    <td className="pl-2 pr-4 py-2 text-right whitespace-nowrap tabular-nums">{r.amount || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </section>
        </div>
      )}

      {step === 4 && result && (
        <section className="neu-card rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-3">
            <FiCheckCircle className="w-8 h-8 text-primary shrink-0" />
            <div>
              <div className="text-lg font-semibold text-foreground">{result.novos} movimento{result.novos === 1 ? '' : 's'} importado{result.novos === 1 ? '' : 's'}</div>
              <div className="text-sm text-muted-foreground">{result.dup} já existia(m){result.invalidas > 0 ? ` · ${result.invalidas} linha(s) inválida(s)` : ''}</div>
            </div>
          </div>
          {result.autoConciliados > 0 && (
            <p className="text-[13px] text-emerald-600 dark:text-emerald-400">{result.autoConciliados} conciliado{result.autoConciliados === 1 ? '' : 's'} automaticamente (100% de confiança — valor e vencimento idênticos).</p>
          )}
          {result.autoCriados > 0 && (
            <p className="text-[13px] text-amber-600 dark:text-amber-400">{result.autoCriados} título{result.autoCriados === 1 ? '' : 's'} criado{result.autoCriados === 1 ? '' : 's'} e baixado{result.autoCriados === 1 ? '' : 's'} sozinho{result.autoCriados === 1 ? '' : 's'} por regra de conciliação.</p>
          )}
          <p className="text-[13px] text-muted-foreground">Os demais movimentos entraram como pendentes na fila de conciliação.</p>
        </section>
      )}
    </SideModal>
  )
}
